import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { AuthProvider, useAuth } from './context/AuthContext';
import { Toaster } from './components/ui/sonner';
import HomePage from './pages/HomePage';
import LoginPage from './pages/LoginPage';
import AdminDashboard from './pages/AdminDashboard';
import TeacherDashboard from './pages/TeacherDashboard';
import BookingPage from './pages/BookingPage';
import { api } from './services/api';
import ResetPasswordPage from './pages/ResetPasswordPage';
import ConfirmLessonPage from './pages/ConfirmLessonPage';
import PortineriaPage from './pages/PortineriaPage';
import MobilePage from './pages/MobilePage';
import { isMobileDevice } from './utils/device';
import '@/App.css';

const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA]">
        <div className="text-center">
          <div className="animate-spin w-8 h-8 border-2 border-[#0A0A0A] border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-[#52525B]">Caricamento...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    // Allow also_admin teachers to access admin routes
    if (user.also_admin && allowedRoles.includes('admin')) {
      return children;
    }
    if (user.role === 'admin' || user.role === 'collaborator' || user.role === 'other') {
      return <Navigate to="/admin" replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }

  // Se l'utente è su smartphone, reindirizza automaticamente all'interfaccia mobile
  if (isMobileDevice()) {
    return <Navigate to="/m" replace />;
  }

  return children;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA]">
        <div className="text-center">
          <div className="animate-spin w-8 h-8 border-2 border-[#0A0A0A] border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-[#52525B]">Caricamento...</p>
        </div>
      </div>
    );
  }

  if (user) {
    // Se l'utente è su smartphone, va sempre all'interfaccia mobile
    if (isMobileDevice()) {
      return <Navigate to="/m" replace />;
    }
    // Don't redirect if also_admin teacher needs to choose role
    if (user.also_admin && user.role === 'teacher' && window.location.pathname === '/login') {
      return children;
    }
    if (user.role === 'admin') {
      return <Navigate to="/admin" replace />;
    }
    if (user.role === 'collaborator' || user.role === 'other') {
      return <Navigate to="/admin" replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

const RootRedirect = () => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA]">
        <div className="text-center">
          <div className="animate-spin w-8 h-8 border-2 border-[#0A0A0A] border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-[#52525B]">Caricamento...</p>
        </div>
      </div>
    );
  }

  if (user) {
    if (isMobileDevice()) return <Navigate to="/m" replace />;
    if (user.role === 'admin') return <Navigate to="/admin" replace />;
    return <Navigate to="/dashboard" replace />;
  }

  return <HomePage />;
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<RootRedirect />} />
          <Route path="/prenota" element={<BookingPage />} />
          <Route path="/portineria" element={<PortineriaPage />} />
          <Route path="/m" element={<MobilePage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/conferma/:token" element={<ConfirmLessonPage />} />
          <Route
            path="/login"
            element={
              <PublicRoute>
                <LoginPage />
              </PublicRoute>
            }
          />
          <Route
            path="/admin"
            element={
              <ProtectedRoute allowedRoles={['admin', 'collaborator', 'other']}>
                <AdminDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute allowedRoles={['teacher']}>
                <TeacherDashboard />
              </ProtectedRoute>
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <GlobalFooter />
      </BrowserRouter>
      <Toaster position="top-right" />
    </AuthProvider>
  );
}

function GlobalFooter() {
  const location = useLocation();
  const [ver, setVer] = useState({ deploy_count: 0, deploy_at: '' });
  useEffect(() => {
    api.getAppVersion().then(res => setVer(res.data || {})).catch(() => {});
  }, []);
  // Nascondi il footer globale sulla pagina mobile /m (ha la sua bottom nav)
  if (location.pathname.startsWith('/m')) return null;
  // Offset base per rendere il numero versione più leggibile (parte da 45000)
  const VERSION_BASE = 45000;
  const versionNumber = (ver.deploy_count || 0) + VERSION_BASE;
  let dateLabel = '';
  if (ver.deploy_at) {
    try {
      const d = new Date(ver.deploy_at);
      if (!isNaN(d)) dateLabel = d.toLocaleDateString('it-IT');
    } catch {}
  }
  return (
    <div className="fixed bottom-0 left-0 right-0 text-center py-2 bg-[#1a1a2e] text-[11px] text-white/70 font-semibold tracking-wider z-50 shadow-inner" data-testid="global-footer">
      GETSOFTWARE &copy;2026 &middot; Lorenzo Folegati Dev &middot; Tel. 3386036469
      {ver.deploy_count > 0 && (
        <span className="ml-3 text-white/50" data-testid="global-footer-version">
          &middot; ver. {versionNumber}{dateLabel ? ` del ${dateLabel}` : ''}
        </span>
      )}
    </div>
  );
}

export default App;
